/** "25–30 min" from an ETA range. */
export function etaText(eta: { from: number; to: number }): string {
  return eta.from === eta.to ? `${eta.from} min` : `${eta.from}–${eta.to} min`;
}

/** "2.1 km" / "800 m". */
export function distanceText(km: number): string {
  if (km < 1) return `${Math.max(50, Math.round((km * 1000) / 50) * 50)} m`;
  return `${km.toFixed(1)} km`;
}

/** "4.3" or null when there are too few ratings to show one. */
export function ratingText(rating: { average: number | null; count: number }): string | null {
  return rating.average === null ? null : rating.average.toFixed(1);
}

/** "0:42" from seconds (voice note length). */
export function clockText(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "7:30 PM" in the phone's time (hand-written: no locale "at" surprises across devices). */
export function clockOfDay(iso: string): string {
  const date = new Date(iso);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Today 7:30 PM" / "Tomorrow 9:00 AM" / "2 Nov, 9:00 AM" for a delivery slot. */
export function slotText(
  iso: string,
  labels: { today: string; tomorrow: string },
  now: Date = new Date(),
): string {
  const date = new Date(iso);
  const day = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const diff = Math.round((day(date) - day(now)) / 86_400_000);
  if (diff === 0) return `${labels.today} ${clockOfDay(iso)}`;
  if (diff === 1) return `${labels.tomorrow} ${clockOfDay(iso)}`;
  return `${date.getDate()} ${MONTHS[date.getMonth()]}, ${clockOfDay(iso)}`;
}

/** "31 Oct, 7:45 PM" for order history. */
export function orderTime(iso: string): string {
  const date = new Date(iso);
  return `${date.getDate()} ${MONTHS[date.getMonth()]}, ${clockOfDay(iso)}`;
}

/** "12 Nov" — credit expiry and refund deadlines. */
export function dayMonth(iso: string): string {
  const date = new Date(iso);
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
}

/** "9:05" left until `iso` (never negative). */
export function timeLeft(iso: string, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.floor((new Date(iso).getTime() - now) / 1000));
  return clockText(seconds);
}
